import { Directive, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Table } from 'primeng/table';
import { Subject, takeUntil } from 'rxjs';
import { ROWS_PER_PAGE_DEFAULT, ROWS_PER_PAGE_OPTIONS } from '../constants/common.constants';

@Directive({
    // global directive
    selector: 'p-table',
    standalone: true
})
export class TableDirective implements OnChanges, OnDestroy {

	@Input() totalRecords: number;
	private readonly destroy$ = new Subject<void>();

	constructor(private readonly table: Table, private readonly translateService: TranslateService){ }

	ngOnChanges(changes: SimpleChanges): void {
	    if (changes['totalRecords'] && this.totalRecords !== undefined) {
	        this.applyTableConfig();
	    }
	    this.table.first = 0;
	}

	ngOnDestroy(): void {
	    this.destroy$.next();
	    this.destroy$.complete();
	}

	private applyTableConfig(): void {
	    if(this.totalRecords > 5){
	        this.table.paginator = true;
	        this.table.showCurrentPageReport = true;
	        this.table.currentPageReportTemplate = this.translateService.instant('TABLE.PAGE_REPORT');
	        this.table.rows = this.getRows(this.totalRecords);
	        this.table.rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS;
	        this.table.sortMode = 'single';
	        this.table.scrollable = true;
	        // this.table.styleClass = 'p-datatable-gridlines';
	        this.table.showLoader = false;
	        this.addSubscriptions();
	    }else{
	        this.table.paginator = false;
	    }
	}

	private addSubscriptions(): void {
	    this.table.onPage.pipe(takeUntil(this.destroy$)).subscribe((event) => {
	        localStorage.setItem('odCurrentTableRows', event.rows.toString());
	    });

	    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
	        this.table.currentPageReportTemplate = this.translateService.instant('TABLE.PAGE_REPORT');
	    });
	}

	private getRows(totalRecords: number): number {
	    const storedRows = parseInt(localStorage.getItem('odCurrentTableRows'));
	    const hasValidStoredRows = Number.isFinite(storedRows) && ROWS_PER_PAGE_OPTIONS.includes(storedRows);
	    let rows = hasValidStoredRows ? storedRows : ROWS_PER_PAGE_DEFAULT;

	    // If a persisted 100-page size leaves a single page for small result sets,
	    // fallback to the default so the rows-per-page selector is usable again.
	    if (rows === 100 && totalRecords > 0 && totalRecords < rows) {
	        rows = ROWS_PER_PAGE_DEFAULT;
	    }

	    if (rows !== storedRows) {
	        localStorage.setItem('odCurrentTableRows', rows.toString());
	    }

	    return rows;
	}
}
